import React, { useState } from "react";
import styles from "./Auth.module.css";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../api"; 
import { notifyUserUpdated } from "../utils/userNotifier";
import { toast } from "react-hot-toast";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();

  // Validate form fields
  const validate = () => {
    const newErrors = {};
    if (!form.email) newErrors.email = "Please fill the Email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Please enter a valid email address";
    if (!form.password) newErrors.password = "Please fill the Password";
    if (!termsAccepted) newErrors.terms = "You must accept the terms and conditions";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Set error for invalid credentials
  const validateCredentials = () => {
    setErrors({ credentials: "Invalid credentials" });
  };

  // Update form state on input change
  const handleChange = (e) => {
    const { type, checked } = e.target;
    if (type === 'checkbox') {
        setTermsAccepted(checked);
        if (errors.terms) setErrors(prev => ({ ...prev, terms: "" }));
    } else {
        setForm({ ...form, [e.target.name]: e.target.value });
        if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: "" }));
    }
    if (errors.credentials) setErrors(prev => ({ ...prev, credentials: "" }));
  };

  // Handle input focus for floating label
  const handleFocus = (field) => {
    setIsFocused({ ...isFocused, [field]: true });
  };

  // Handle input blur for floating label
  const handleBlur = (field) => {
    setIsFocused({ ...isFocused, [field]: false });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);

    try {
      // Get guest cart/wishlist from localStorage before login (defensive)
      const safeParse = (val) => {
        try {
          return JSON.parse(val);
        } catch (e) {
          return null;
        }
      };

      const fromPersistRoot = safeParse(localStorage.getItem('persist:root')) || {};

      const guestCartItems = (() => {
        // Try several possible locations/formats for guest cart
        const candidates = [
          // redux-persist root
          fromPersistRoot.cart ? safeParse(fromPersistRoot.cart) : null,
          // direct keys
          safeParse(localStorage.getItem('guestCartItems')),
          safeParse(localStorage.getItem('guestCart')),
          safeParse(localStorage.getItem('guest_cart')),
          safeParse(localStorage.getItem('cartItems')),
          safeParse(localStorage.getItem('cart'))
        ];
        for (const c of candidates) {
          if (Array.isArray(c)) return c;
          if (c && Array.isArray(c.items)) return c.items;
        }
        return [];
      })();

      const guestWishlistItems = (() => {
        // Try several possible locations/formats for guest wishlist
        const candidates = [
          fromPersistRoot.wishlist ? safeParse(fromPersistRoot.wishlist) : null,
          safeParse(localStorage.getItem('guestWishlistItems')),
          safeParse(localStorage.getItem('guestWishlist')),
          safeParse(localStorage.getItem('guest_wishlist')),
          safeParse(localStorage.getItem('wishlistItems')),
          safeParse(localStorage.getItem('wishlist'))
        ];
        for (const c of candidates) {
          if (Array.isArray(c)) return c;
          if (c && Array.isArray(c.items)) return c.items;
        }
        return [];
      })();

      // Use centralized apiFetch for login
      const data = await apiFetch('/api/users/login', {
        method: "POST",
        body: form,
      });

      // Check for user ID (backend sends 'id')
      if (!data.id) {
        toast.error("Login failed: Missing user ID in response.", { position: "top-center" });
        setIsLoading(false);
        return;
      }

      if (data.token) {
        // Store token and user data in localStorage
        localStorage.setItem("token", data.token);
        localStorage.setItem(
          "user",
          JSON.stringify({
            id: data.id,
            name: data.name,
            role: data.role,
            email: form.email,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            phone: data.phone || '',
            profileImage: data.profileImage,
          })
        );

            // Notify same-tab and other tabs that user has updated
            try { notifyUserUpdated(JSON.parse(localStorage.getItem('user'))); } 
            catch (err) { }

        // Show success toast
        toast.success("Login successful", { position: "top-center" });

        // Dynamically import store and thunks for merging
        const { store } = await import('../redux/Store');
        const { fetchUserWishlist, addProductToWishlist } = await import('../redux/WishlistSlice');
        const { fetchUserCart, addProductToCart, updateCartItemQuantity } = await import('../redux/cartSlice');
        const dispatch = store.dispatch;

        // Fetch user's existing data
        await dispatch(fetchUserWishlist());
        await dispatch(fetchUserCart());
        const serverWishlist = store.getState().wishlist.items;
        const serverCart = store.getState().cart.items;

        // Merge guest wishlist silently
        for (const guestItem of guestWishlistItems) {
          if (!serverWishlist.some(serverItem => serverItem.id === guestItem.id)) {
            await dispatch(addProductToWishlist(guestItem, { showNotification: false }));
          }
        }

        // Merge guest cart silently
        for (const guestItem of guestCartItems) {
          const serverItem = serverCart.find(serverCartItem => serverCartItem.id === guestItem.id);
          if (serverItem) {
            if (guestItem.quantity > serverItem.quantity) {
              await dispatch(updateCartItemQuantity(guestItem, guestItem.quantity, { showNotification: false }));
            }
          } else {
            await dispatch(addProductToCart(guestItem, guestItem.quantity, { showNotification: false }));
          }
        }
      } else {
         throw new Error("Login response missing token.");
      }

      // Navigate to home page after merge is complete
      navigate("/");

    } catch (err) {
      toast.error(err.message || "Login failed", { position: "top-center" });
      validateCredentials();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.authContainer}>
      {/* Background Elements */}
      <div className={styles.boutiqueBackground}>
        <div className={styles.luxuryPattern}></div>
        <div className={styles.crystalShape1}></div>
        <div className={styles.crystalShape2}></div>
        <div className={styles.crystalShape3}></div>
        <div className={styles.goldAccent1}></div>
        <div className={styles.goldAccent2}></div>
      </div>
      <div className={styles.floatingShapes}>
        <div className={styles.shape1}></div>
        <div className={styles.shape2}></div>
        <div className={styles.shape3}></div>
        <div className={styles.shape4}></div>
      </div>
      <div className={styles.particles}>
        {[...Array(12)].map((_, i) => (
          <div key={i} className={styles.particle} style={{ '--delay': `${i * 0.3}s`, '--duration': `${12 + i * 1.5}s`, left: `${Math.random() * 100}%` }}></div>
        ))}
      </div>

      {/* Auth Card */}
      <div className={`card ${styles.authCard}`}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>Welcome Back</h3>
        </div>

        <div className="card-body p-4 p-md-5 p-lg-5">
          <form onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className={`mb-4 ${styles.inputContainer}`}>
              <label className={`form-label fw-semibold ${styles.floatingLabel} ${(form.email || isFocused.email) ? styles.labelFloating : ''}`}>
                Email address
              </label>
              <div className={`input-group ${styles.inputGroup}`}>
                <span className={`input-group-text ${styles.inputIcon} ${isFocused.email ? styles.inputIconFocused : ''}`}>
                  <FaEnvelope />
                </span>
                <input
                  type="email"
                  className={`form-control ${styles.formControl}`}
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  onFocus={() => handleFocus('email')}
                  onBlur={() => handleBlur('email')}
                  placeholder=" "
                />
                <div className={styles.inputUnderline}></div>
              </div>
              {errors.email && <div className={`text-danger ${styles.errorText}`}>{errors.email}</div>}
            </div>

            {/* Password Field */}
            <div className={`mb-4 ${styles.inputContainer}`}>
              <label className={`form-label fw-semibold ${styles.floatingLabel} ${(form.password || isFocused.password) ? styles.labelFloating : ''}`}>
                Password
              </label>
              <div className={`input-group ${styles.inputGroup}`}>
                <span className={`input-group-text ${styles.inputIcon} ${isFocused.password ? styles.inputIconFocused : ''}`}>
                  <FaLock />
                </span>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${styles.formControl}`}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  onFocus={() => handleFocus('password')}
                  onBlur={() => handleBlur('password')}
                  placeholder=" "
                />
                <button
                  className={`input-group-text ${styles.inputIcon} ${isFocused.password ? styles.inputIconFocused : ''}`}
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
                <div className={styles.inputUnderline}></div>
              </div>
              {errors.password && <div className={`text-danger ${styles.errorText}`}>{errors.password}</div>}
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className={styles.termsContainer}>
              <input
                type="checkbox"
                id="loginTermsCheck"
                name="terms"
                checked={termsAccepted}
                onChange={handleChange}
                className={styles.termsCheckbox}
              />
              <label htmlFor="loginTermsCheck" className={styles.termsLabel}>
                I agree to the <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link> and <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
              </label>
            </div>
            {errors.terms && <div className={`text-danger ${styles.errorText} mt-1`}>{errors.terms}</div>}


            {/* Error Alert */}
            {errors.credentials && (
              <div className={`alert alert-danger ${styles.alert}`} role="alert">
                <div className={styles.alertContent}>
                  <span className={styles.alertIcon}>⚠️</span>
                  {errors.credentials}
                </div>
                <div className={styles.alertShine}></div>
              </div>
            )}

            {/* Submit Button */}
            <div className="d-grid mb-4">
              <button 
                type="submit" 
                className={`${styles.btn} ${isLoading ? styles.btnLoading : ''}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className={styles.spinner}></span>
                    <span className={styles.btnText}>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span className={styles.btnText}>Sign In</span>
                    <div className={styles.btnShine}></div>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Links */}
          <div className="text-center mb-3">
            <Link to="/forget" className={`${styles.authLink} ${styles.forgotLink}`}>
              <span>Forgot your password?</span>
              <div className={styles.linkUnderline}></div>
            </Link>
          </div>

          {/* Divider */}
          <div className={styles.divider}>
            <span>or</span>
          </div>

          {/* Sign Up Link */}
          <div className="text-center mt-4">
            <span className={styles.signupText}>Don't have an account? </span>
            <Link to="/signup" className={styles.authLink}>
              <span>Create account</span>
              <div className={styles.linkUnderline}></div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}